import { HTTP_INTERCEPTORS, HttpClient, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { APP_INITIALIZER, ErrorHandler, ModuleWithProviders, NgModule } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from "@angular/platform-browser/animations";
import { ShellService } from './services/shell.service';
import { OKTA_AUTH, OKTA_CONFIG, OktaAuthModule } from '@okta/okta-angular';
import OktaAuth from '@okta/okta-auth-js';
import { oktaConfig } from '../okta.config';
import { TecComUserService } from './services/teccom-user.service';
import { environment } from 'src/environments/environment';
import { JwtModule, JWT_OPTIONS } from '@auth0/angular-jwt';
import { LanguageService, SharedStatesModule } from 'shared-states';
import { SupportedLanguageService } from './services/supported-language.service';
import { TranslateLoader, TranslateModule, TranslateService } from '@ngx-translate/core';
import { TranslationsLoader } from './loaders/translations-loader';
import { PUBLIC_REMOTES_ROUTES } from '../routes/public.routes';
import { PROTECTED_REMOTES_ROUTES } from '../routes/protected.routes';
import { Microfrontend } from './models/types/microfrontend.types';
import { CUSTOMER_REMOTES_ROUTES } from '../routes/customer.routes';
import { NotificationService } from './services/notification.service';
import { CookieStorageService } from '../shared/services/cookie-storage.service';
import { RemoteTranslations } from './models/remote-translations.model';
import { InternalTokenInterceptor } from './interceptors/internal-token.interceptor';
import { GlobalErrorHandler } from './services/global-error-handler';
import { OrganizationService } from './services/organization.service';
import { RoutePerformanceService } from './services/route-performance.service';

const oktaAuth = new OktaAuth(oktaConfig);

export function createTranslateLoader(http: HttpClient) {
  const remoteTranslation = [...PROTECTED_REMOTES_ROUTES, ...PUBLIC_REMOTES_ROUTES, ...CUSTOMER_REMOTES_ROUTES].map((route: Microfrontend) => {
    return <RemoteTranslations>{
      keyPrefix: route.displayName,
      uri: route.translationScope ? undefined : `${route.remoteUri}${route.i18nPath}`,
      scope: route.translationScope
    }
  });
  return new TranslationsLoader(http, remoteTranslation);
}

export function languageInitializerFactory(
  languageService: LanguageService,
  supportedLanguageService: SupportedLanguageService,
  storageService: CookieStorageService
) {
  return async () => {

    const [languages, storedLang] : any = await Promise.all([
      supportedLanguageService.getSupportedLanguage('assets/i18n/languages.json'),
      getStorageLanguage(storageService)
    ]);

    languageService.init(languages.languages, 'en');

    const supportedLangs = languages.languages.map((l: any) => l.value);
    const browserLang = navigator.language?.split('-')[0] ?? 'en';

    if (!storedLang && supportedLangs.includes(browserLang)) {
      storageService.set('Language', browserLang);
      languageService.use(browserLang);
    }

    if (storedLang && storedLang !== 'en') {
      languageService.use(storedLang);
    }
  };
}

function getStorageLanguage(storageService: CookieStorageService) : Promise<string> {
  return new Promise((resolve) => {
    const maxAttempt = 5;
    const getLanguage = (attempt: number) => {

      if (storageService.get('Language') || attempt === maxAttempt){
        resolve(storageService.get('Language'));
      }
      else{
        setTimeout(() => getLanguage(attempt + 1), 100);
      }
    }
    getLanguage(0);
  });
}

export function jwtOptionsFactory(oktaAuth: OktaAuth) {
  return {
    tokenGetter: () => {
      return oktaAuth.getAccessToken();
    },
    allowedDomains: environment.allowedDomains
  };
}

@NgModule({
  declarations: [],
  imports: [
    BrowserModule,
    BrowserAnimationsModule,
    OktaAuthModule,
    JwtModule.forRoot({
      jwtOptionsProvider: {
        provide: JWT_OPTIONS,
        useFactory: jwtOptionsFactory,
        deps: [OKTA_AUTH]
      }
    }),
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: (createTranslateLoader),
        deps: [HttpClient]
      }
    }),
    SharedStatesModule
  ],
  exports: [
    BrowserModule,
    BrowserAnimationsModule,
    OktaAuthModule,
    TranslateModule,
    SharedStatesModule
  ],
  providers: [
    ShellService,
    TecComUserService,
    TranslateService,
    OrganizationService,
    LanguageService,
    SupportedLanguageService,
    CookieStorageService,
    NotificationService,
    {
      provide: OKTA_CONFIG,
      useValue: { oktaAuth }
    },
    {
      provide: APP_INITIALIZER,
      useFactory: languageInitializerFactory,
      deps: [LanguageService, SupportedLanguageService, CookieStorageService],
      multi: true,
    },
    {
      provide: APP_INITIALIZER,
      useFactory: (rps: RoutePerformanceService) => () => rps.init(),
      deps: [RoutePerformanceService],
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS, useClass: InternalTokenInterceptor, multi: true
    },
    {
      provide: 'appInsightConfig',
      useValue: {
        instrumentationKey: environment.appInsights.instrumentationKey,
        enableAutoRouteTracking: false,
        enableAjaxTracking: true,
        enableAjaxPerfTracking: true,
        ajaxPerfLookupDelay: 25,
        distributedTracingMode: 2,
        enableCorsCorrelation: false,
        enableCorrelationHeaders: true,
        correlationHeaderExcludedDomains: [
          '*.okta.com',
          'login.tecalliance.net',
          'login.qa.tecalliance.cloud',
          '*.tecalliance.cn',
          '*.tecalliance.services'
        ],
        maxBatchInterval: 5000,
        // Debug logging — controlled per environment via enableDebug flag
        enableDebug: environment.appInsights.enableDebug,
        loggingLevelConsole: environment.appInsights.enableDebug ? 2 : 0
      }
    },
    {
      provide: 'prefix',
      useValue: 'shell'
    },
    {
      provide: ErrorHandler,
      useClass: GlobalErrorHandler
    },
    provideHttpClient(withInterceptorsFromDi()) //FYI: Old HttpClientModule
  ]
})
export class ShellCoreModule {
  static forRoot(): ModuleWithProviders<ShellCoreModule> {
    return {
      ngModule: ShellCoreModule
    };
  }
}
